/**
 * Custom Events Tracking — collection page: filters, sort and pagination.
 *
 * filters_open · filters_apply · filters_view · filters_clear
 * sortby_open · sortby_apply · sortby_close · load_more
 *
 * Companion to assets/analytics.js, which owns the dataLayer push helpers.
 * Split out to keep every file under the 500-line ceiling and to group the
 * collection-grid hooks, which all share the Tagalys facet DOM.
 */
(function () {
  'use strict';

  var W = window;
  /* Refuse a second execution of this file. The `inited` guard below is
     module-scoped, so it cannot see a duplicate <script> include — and every
     hook here would then be registered twice, silently doubling every event.
     Mirrors the dataset.*Ready guards the theme's own controllers use. */
  if (W.__whittardAnalyticsCollection) return;
  W.__whittardAnalyticsCollection = true;
  var A = W.WhittardAnalytics;
  if (!A) return;
  var push = A.push;

  /* Matches the md breakpoint the rest of the theme branches on in JS
     (snippets/plp-ui.liquid, layout/theme.liquid's header measurement). */
  var BP_MD = 1024;

  /* ═══════════════════════════════════════════════
     Filters — filters_open, filters_apply, filters_view, filters_clear
  ═══════════════════════════════════════════════ */

  /* The facet's display name lives on the accordion item that wraps it
     (data-filter-label, set by snippets/collection-tagalys-facets.liquid).
     Falls back to the accordion's own trigger text so a facet rendered before
     that attribute existed still reports something meaningful. */
  function facetType(el) {
    var item = el.closest('[data-accordion-item]');
    if (!item) return el.getAttribute('data-facet-key') || '';
    var label = item.getAttribute('data-filter-label');
    if (label) return label;
    var trigger = item.querySelector('[data-accordion-trigger] span');
    return trigger ? trigger.textContent.trim() : (el.getAttribute('data-facet-key') || '');
  }

  /* The visible option label, which is what the report needs — not the raw
     Tagalys value. Image tiles and checkboxes both carry their own aria-label /
     label text; data-facet-value is the last resort. */
  function facetOption(el) {
    /* The option's own name, set by snippets/collection-tagalys-facets.liquid.
       Preferred over the label's text because the visible label also carries the
       product count — "Loose Leaf Tea, 75g (3)" — which is not part of the option
       (QA, 2026-09-01). The text fallbacks below only apply to markup rendered
       before that attribute existed. */
    var explicit = el.getAttribute('data-facet-label');
    if (explicit) return explicit;

    var label = el.closest('label');
    if (label) {
      var text = label.textContent.trim();
      if (text) return text;
    }
    return el.getAttribute('aria-label') || el.getAttribute('data-facet-value') || '';
  }

  function watchFilters() {
    document.addEventListener('click', function (e) {
      /* "All Filters", and the facet quick-links sitting next to it, both open
         the same drawer. */
      if (e.target.closest('[data-filter-trigger], [data-drawer-open="filters-drawer"]')) {
        push('filters_open');
        return;
      }
      if (e.target.closest('[data-view-results]')) {
        push('filters_view');
        return;
      }
      if (e.target.closest('[data-clear-filters], [data-inline-clear]')) {
        push('filters_clear');
        return;
      }
      /* Image-tile facets are buttons, not inputs, so they never fire `change`.
         aria-pressed is read before the facet controller flips it, so "false"
         here means this click is selecting the option. */
      var tile = e.target.closest('[data-image-tile][data-facet-key]');
      if (tile && tile.getAttribute('aria-pressed') !== 'true') {
        push('filters_apply', { type: facetType(tile), option: facetOption(tile) });
      }
    }, true);

    /* Checkbox facets. Only a selection is an "applied" filter — deselecting is
       not filters_apply, and is not filters_clear either (that is the Clear
       button, per the spec's own trigger wording). */
    document.addEventListener('change', function (e) {
      var box = e.target.closest('input[type="checkbox"][data-facet-key]');
      if (!box || !box.checked) return;
      push('filters_apply', { type: facetType(box), option: facetOption(box) });
    }, true);
  }

  /* ═══════════════════════════════════════════════
     Sort — sortby_open, sortby_apply, sortby_close
     Desktop is a positioned panel driven by aria-expanded on the trigger;
     mobile is the shared sort-drawer. Both paths are covered.
  ═══════════════════════════════════════════════ */

  function watchSort() {
    document.addEventListener('click', function (e) {
      var option = e.target.closest('[data-sort-option]');
      if (!option) return;
      var label = option.textContent.trim();
      push('sortby_apply', { option: label || option.getAttribute('data-sort-option') });
    }, true);

    /* Below md the trigger opens the shared sort-drawer through the controller's
       own JS call rather than a [data-drawer-open] attribute, and never touches
       aria-expanded — so the desktop observer below cannot see it. Gated on the
       same 1024px breakpoint snippets/plp-ui.liquid branches on, so exactly one
       of the two paths reports any given open. Read per click rather than cached,
       so a resize across the breakpoint cannot leave this reporting the wrong
       path. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-sort-trigger]')) return;
      if (W.innerWidth >= BP_MD) return;
      push('sortby_open');
    }, true);
    document.addEventListener('drawer:close', function (e) {
      if (e.detail && e.detail.id === 'sort-drawer') push('sortby_close');
    });

    var trigger = document.querySelector('[data-sort-trigger]');
    if (!trigger) return;
    var wasOpen = trigger.getAttribute('aria-expanded') === 'true';
    new MutationObserver(function () {
      var isOpen = trigger.getAttribute('aria-expanded') === 'true';
      if (isOpen === wasOpen) return;
      wasOpen = isOpen;
      /* Covers every desktop close path — option picked, outside click, Escape. */
      push(isOpen ? 'sortby_open' : 'sortby_close');
    }).observe(trigger, { attributes: true, attributeFilter: ['aria-expanded'] });
  }

  /* ═══════════════════════════════════════════════
     Pagination — load_more
     One delegated hook covers all four renderers (collection, search, and both
     paginated Liquid fallbacks), which each emit their own [data-load-more].
  ═══════════════════════════════════════════════ */

  function watchLoadMore() {
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-load-more]')) push('load_more');
    }, true);
  }

  /* Isolated per watcher, and idempotent, for the same reasons as
     assets/analytics.js's own init — see the notes there. */
  var inited = false;

  function safe(name, fn) {
    try { fn(); } catch (e) {
      if (W.console) W.console.warn('[analytics-collection] ' + name + ' failed to initialise', e);
    }
  }

  function init() {
    if (inited) return;
    inited = true;
    safe('filters', watchFilters);
    safe('sort', watchSort);
    safe('loadMore', watchLoadMore);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
